import React, { useEffect } from "react";
import { connect } from "react-redux";
import Button from "../../Button";
import { getAreasById, getCinemaEntities, getStates, getStatesById } from "../../../reducers";
import { filterCinemasNearLatLng, PositionType } from "../../../utils/location";
import { CinemaType } from "../../../types/entities";
import { areaSelectorSelectArea } from "../../../actions/creators/ui";
import RightSidePanel from "../RightSidePanel";

interface Props {
    animate?: boolean;
    areasById: { [key: string]: { id: number, name: string } };
    cinemaConcept?: 'traditional' | 'platinum' | 'market' | 'all';
    cinemasById: { [key: number]: CinemaType };
    cinemaSelect?: boolean;
    states: Array<{ id: number, name: string, areas: Array<{ id: number, name: string }> }>;
    statesById: { [key: string]: { id: number, name: string, areas: Array<{ id: number, name: string }> } };
    title?: string;
    onAreaSelect: (type: string, id: number, args?: { [key: string]: any }) => void;
    onClose: () => void;
    onSave?: (data: { cinemaConcept: 'traditional' | 'platinum' | 'market' | 'all', area?: { type: string, id: number } }) => void;
};

const ShowtimesAreaSelector = (props: Props) => {
    const [cinemaConcept, setCinemaConcept] = React.useState<'traditional' | 'platinum' | 'market' | 'all'>(props.cinemaConcept || 'all');
    const [cinemaId, setCinemaId] = React.useState<number | undefined>();
    const [stateId, setStateId] = React.useState<number | undefined>();
    const [areaId, setAreaId] = React.useState<string>('');
    const [nearestCinema, setNearestCinema] = React.useState<CinemaType | null>(null);
    const [location, setLocation] = React.useState<PositionType | null>(null);
    const [locationError, setLocationError] = React.useState<string | null>(null);

    useEffect(() => {
        if (!location) {
            return;
        }

        const nearestCinemas = filterCinemasNearLatLng(Object.values(props.cinemasById), location.lat, location.lng);
        if (!nearestCinemas.length) {
            setLocationError('No se encontraron cines cercanos a tu ubicación');
            return;
        }

        setNearestCinema(nearestCinemas[0]);
        setStateId(nearestCinemas[0].state.id);
        setAreaId('area-' + nearestCinemas[0].area.id);
    }, [location]);

    function onSave() {
        const parts = areaId.toString().split('-');
        const type = parts[0];
        const id = parseInt(parts[1], 10);
        
        props.onAreaSelect(type, id, { context: 'global' });
        if (props.onSave) {
            props.onSave({
                cinemaConcept,
                area: { type, id },
            });
        }
        props.onClose();
    }

    function onLocationRequest() {
        if (!navigator.geolocation) {
            return;
        }
        navigator.geolocation.getCurrentPosition(
            position => {
                console.log('Got location', position);
                setLocation({ lat: position.coords.latitude, lng: position.coords.longitude });
            },
            error => {
                console.error('Error getting location', error);
                setLocation(null);
                setLocationError(error.message);
            }
        );
    }

    function getCinemas(): Array<CinemaType> {
        return Object.values(props.cinemasById).filter(cinema => {
            const area = areaId.split('-');
            return 'state' === area[0]
                ? cinema.state.id === parseInt(area[1], 10)
                : cinema.area.id === parseInt(area[1], 10);
        });
    }

    return <RightSidePanel
        animate={true}
        onClose={props.onClose}
        title={props.title || 'Filtrar cines'}
    >
        <div className='text-center'>
            {locationError && <>
                <p className="my-5">No se pudo obtener tu ubicación. Selecciona tu {props.cinemaSelect ? 'cine' : 'zona'} manualmente:</p>
            </>}

            {location && nearestCinema && props.areasById && <p>Tu zona más cercana es <strong>{props.areasById[nearestCinema.area.id].name}</strong></p>}

            {!locationError && !location && <>
                <p className="my-5">
                    <>Selecciona el <strong>estado o zona</strong> donde quieres ver los cines disponibles.</>
                </p>
                <Button primary block onClick={onLocationRequest}>Encontrar mi ubicación automáticamente</Button>
            </>}
        </div>

        {!locationError && <p className="mt-5">O selecciona tu {props.cinemaSelect ? 'cine' : 'zona'} manualmente:</p>}
        
        <div className="form-group">
            <label>Tu estado</label>
            <select className="form-control" value={stateId} onChange={(e) => setStateId(parseInt(e.target.value, 10))}>
                <option value="">Selecciona un estado</option>
                {props.states.map((state) => <option key={state.id} value={state.id}>{state.name}</option>)}
            </select>
        </div>
        <div className="form-group">
            <label>Tu zona</label>
            <select className="form-control" value={areaId} onChange={(e) => setAreaId(e.target.value)} disabled={!stateId}>
                <option value="">Selecciona una zona</option>
                {stateId && <>
                    {!props.cinemaSelect && props.statesById[stateId].areas.length > 1 && <option value={`state-${stateId}`}>Todo {props.statesById[stateId].name}</option>}
                    {props.statesById[stateId].areas.map((area) => <option key={area.id} value={`area-${area.id}`}>{area.name}</option>)}
                </>}
            </select>
        </div>

        {props.cinemaSelect && <div className="form-group">
            <label>Tu cine favorito</label>
            <select className="form-control" value={cinemaId} onChange={(e) => setCinemaId(parseInt(e.target.value, 10))} disabled={!areaId}>
                <option value="">Selecciona un cine</option>
                {areaId && getCinemas().map(cinema => <option key={cinema.id} value={cinema.id}>{cinema.name}</option>)}
            </select>
        </div>}

        {!props.cinemaSelect && props.cinemaConcept !== undefined && !!areaId && <div className="mt-5">
            <p>Selecciona el tipo de cine que deseas ver:</p>
            <select className="form-control" value={cinemaConcept} onChange={(e) => setCinemaConcept(e.target.value as any)}>
                <option value="all">Todos</option>
                <option value="traditional">Tradicional</option>
                <option value="platinum">Platino</option>
                <option value="market">Market</option>
            </select>
        </div>}

        <div className="mt-5">
            <Button primary block onClick={onSave} disabled={!stateId || !areaId}>Aplicar</Button>
        </div>
    </RightSidePanel>;
}

const mapStateToProps = (state) => {
    return {
        areasById: getAreasById(state),
        cinemasById: getCinemaEntities(state),
        states: getStates(state),
        statesById: getStatesById(state),
    }
};

const mapDispatchToProps = (dispatch) => {
    return {
        onAreaSelect: (type: string, id: number, args?: { [key: string]: any }) => dispatch(areaSelectorSelectArea(type, id, args)),
    };
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(ShowtimesAreaSelector);